import { useState, useCallback, useRef } from 'react';

import { OrganizationInformationStructure } from 'doxecure-commons/doxecureTypes';
import { TSError } from 'foundation-ts/tserrors';
import { Resp } from 'foundation-ts/tsrequest';
import { Nullable } from 'foundation-ts/types';

import apiClient from '@/services/apiClient';
import { $ok, $totype } from 'foundation-ts/commons';
import { checkCorporationIdentifier } from 'doxecure-commons/doxecureFunctions';

interface UseSiretReturn {
    corporationInformation: Nullable<OrganizationInformationStructure>;
    isLoadingCI: boolean;
    errorCI: string | null;
    handleSiretChange: (value: string) => void;
}

const DEBOUNCE_DELAY = 500;

export function useSignupOrganizationInformation(): UseSiretReturn {
    const [corporationInformation, setCorporationInformation] = useState<OrganizationInformationStructure | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCorporationInformation = useCallback(async (siret: string) => {
        setIsLoading(true);
        setError(null);

        try {
            const [resp, status] = await apiClient.getOrganizationInformation(siret, 2000);
            switch (status) {
                case Resp.OK:
                    const ci = $totype<OrganizationInformationStructure>(resp);
                    if (!!ci?.active) {
                        setCorporationInformation($totype<OrganizationInformationStructure>(resp));
                    } else {
                        TSError.throw("Etablissement fermé");
                    }
                    break;
                case Resp.BadRequest:
                    TSError.throw("SIRET invalide");
                case Resp.TimeOut:
                    TSError.throw("Le service ne répond pas. Veuillez réessayer dans quelques instants.");
                default:
                    TSError.throw("Le ");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur inconnue');
            setCorporationInformation(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSiretChange = useCallback((value: string) => {
        if (debounceTimer.current) { clearTimeout(debounceTimer.current); }

        setCorporationInformation(null);
        setError(null);
        const siret = checkCorporationIdentifier(value.trim().replace(/\s/g, ''), "SIRET", "FR");
        if (!$ok(siret)) { return; }

        debounceTimer.current = setTimeout(() => {
            fetchCorporationInformation(value);
        }, DEBOUNCE_DELAY);
    }, [fetchCorporationInformation]);

    return { corporationInformation, isLoadingCI: isLoading, errorCI: error, handleSiretChange };
}